// assets/custom-design-order.js
import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';

const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const MM_PER_INCH = 25.4;

/**
 * @typedef {object} CustomDesignOrderRefs
 * @property {HTMLFormElement} form
 * @property {HTMLSelectElement} sizeSelect
 * @property {HTMLElement} priceDisplay
 * @property {HTMLInputElement} fileInput
 * @property {HTMLInputElement} metaInput
 * @property {HTMLElement} statusMessage
 * @property {HTMLInputElement} quantityInput
 * @property {HTMLButtonElement} submitButton
 * @property {HTMLElement} formStatus
 */

/**
 * Standalone "pick a size, upload artwork, add to cart" flow for a single
 * made-to-order product — the design comes from the customer's upload, so
 * the only real choice is size (the product's variants).
 * @extends Component<CustomDesignOrderRefs>
 */
class CustomDesignOrder extends Component {
  /** @type {number} */
  #fileCheckId = 0;

  /** @type {boolean} */
  #submitting = false;

  handleSizeChange() {
    const { sizeSelect, priceDisplay } = this.refs;
    const option = sizeSelect.selectedOptions[0];
    priceDisplay.textContent = option?.dataset.price ?? '';
    sizeSelect.setCustomValidity('');
  }

  /** @param {Event} event */
  handleFileChange(event) {
    const { fileInput } = this.refs;
    const file = fileInput.files?.[0];
    const requestId = ++this.#fileCheckId;

    if (!file) {
      this.#resetFileStatus();
      return;
    }

    if (!ALLOWED_TYPES.includes(file.type)) {
      this.#setFileInvalid(this.dataset.badTypeText ?? 'Unsupported file type.');
      return;
    }

    const maxBytes = (Number(this.dataset.maxFileSizeMb) || 20) * 1024 * 1024;
    if (file.size > maxBytes) {
      this.#setFileInvalid(this.dataset.tooLargeText ?? 'File is too large.');
      return;
    }

    this.#setFileChecking();

    this.#readImageDimensions(file)
      .then(({ width, height }) => {
        if (requestId !== this.#fileCheckId) return;
        this.#applyDimensions(width, height);
      })
      .catch(() => {
        if (requestId !== this.#fileCheckId) return;
        this.#setFileInvalid(this.dataset.readErrorText ?? "We couldn't read that image.");
      });
  }

  /**
   * @param {File} file
   * @returns {Promise<{width: number, height: number}>}
   */
  async #readImageDimensions(file) {
    if ('createImageBitmap' in window) {
      const bitmap = await createImageBitmap(file);
      const { width, height } = bitmap;
      bitmap.close();
      return { width, height };
    }

    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const image = new Image();
      image.onload = () => {
        URL.revokeObjectURL(url);
        resolve({ width: image.naturalWidth, height: image.naturalHeight });
      };
      image.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('image-decode-failed'));
      };
      image.src = url;
    });
  }

  /**
   * @param {number} width
   * @param {number} height
   */
  #applyDimensions(width, height) {
    const printWidthMm = Number(this.dataset.printWidthMm) || 0;
    const printHeightMm = Number(this.dataset.printHeightMm) || 0;
    const minDpi = Number(this.dataset.minDpi) || 0;

    let dpi = null;
    if (printWidthMm > 0 && printHeightMm > 0) {
      const dpiForWidth = width / (printWidthMm / MM_PER_INCH);
      const dpiForHeight = height / (printHeightMm / MM_PER_INCH);
      dpi = Math.round(Math.min(dpiForWidth, dpiForHeight));
    }

    const { metaInput } = this.refs;
    metaInput.value = dpi !== null ? `${width}×${height}px (~${dpi} DPI)` : `${width}×${height}px`;

    if (minDpi > 0 && dpi !== null && dpi < minDpi) {
      this.#setFileInvalid(
        `${this.dataset.lowDpiText ?? 'This image is lower resolution than we recommend.'} ` +
          (this.dataset.resolutionRecommendationText ?? '[dpi] DPI; recommended minimum: [min] DPI')
            .replace('[dpi]', String(dpi))
            .replace('[min]', String(minDpi))
      );
      return;
    }

    const detail = dpi !== null ? `${width}×${height}px, ~${dpi} DPI` : `${width}×${height}px`;
    this.#setFileValid(`${this.dataset.validText ?? 'Looks good!'} (${detail})`);
  }

  #setFileChecking() {
    const { fileInput, statusMessage } = this.refs;
    const text = this.dataset.checkingText ?? 'Checking your image…';
    fileInput.setCustomValidity(text);
    statusMessage.textContent = text;
    statusMessage.dataset.state = 'checking';
  }

  /** @param {string} message */
  #setFileInvalid(message) {
    const { fileInput, statusMessage } = this.refs;
    fileInput.setCustomValidity(message);
    statusMessage.textContent = message;
    statusMessage.dataset.state = 'invalid';
  }

  /** @param {string} message */
  #setFileValid(message) {
    const { fileInput, statusMessage } = this.refs;
    fileInput.setCustomValidity('');
    statusMessage.textContent = message;
    statusMessage.dataset.state = 'valid';
  }

  #resetFileStatus() {
    const { fileInput, metaInput, statusMessage } = this.refs;
    fileInput.setCustomValidity('');
    metaInput.value = '';
    statusMessage.textContent = '';
    delete statusMessage.dataset.state;
  }

  /** @param {Event} event */
  handleSubmit(event) {
    event.preventDefault();
    if (this.#submitting) return;

    const { form, sizeSelect, formStatus, submitButton } = this.refs;

    if (!sizeSelect.value) {
      sizeSelect.setCustomValidity(this.dataset.selectSizeText ?? 'Please choose a size first.');
    } else {
      sizeSelect.setCustomValidity('');
    }

    if (!form.reportValidity()) return;

    this.#submitting = true;
    submitButton.disabled = true;
    formStatus.textContent = '';
    delete formStatus.dataset.state;

    const formData = new FormData(form);
    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, fetchCfg)
      .then((response) => response.json())
      .then((data) => {
        if (data.status) {
          throw new Error(data.description || 'cart-add-failed');
        }
        formStatus.textContent = this.dataset.addedText ?? 'Added to cart!';
        formStatus.dataset.state = 'success';
        window.location.assign(Theme.routes.cart_url || '/cart');
      })
      .catch(() => {
        formStatus.textContent = this.dataset.errorText ?? 'Something went wrong. Please try again.';
        formStatus.dataset.state = 'error';
        this.#submitting = false;
        submitButton.disabled = false;
      });
  }
}

customElements.define('custom-design-order-component', CustomDesignOrder);
